import { Alert, Button, Select } from '@mantine/core';
import { api } from '~/trpc-extra/api';
import { dayNames, dayNamesAbbreviations, timeOptions, timezoneList } from '~/common/cron/constants';
import DropdownChecklist from '../common/DropdownChecklist';
import { notifications } from '@mantine/notifications';
import { useForm } from '@mantine/form';
import type { InternalSchedule } from '~/common/cron/crontab-converter';
import { cronToSchedule, scheduleToCron } from '~/common/cron/crontab-converter';
import { IconAlertCircle, IconDeviceFloppy, IconExternalLink, IconTrash } from '@tabler/icons-react';

export default function TestSchedule({
  testSuiteId,
  selectedCronTab,
  selectedTimezoneIana,
  onOpenSuiteSettings,
  onSubmit,
}: {
  testSuiteId: string;
  selectedCronTab?: string;
  selectedTimezoneIana?: string;
  onOpenSuiteSettings: () => void;
  onSubmit: () => void;
}) {
  const trpcUtil = api.useUtils();

  const { mutateAsync: upsertSchedule, isPending: isUpsertingSchedule } = api.testSchedule.upsert.useMutation({
    onSuccess: async () => {
      await trpcUtil.testSchedule.get.invalidate({ testSuiteId });
    },
  });

  const { mutateAsync: deleteSchedule, isPending: isDeletingSchedule } = api.testSchedule.delete.useMutation({
    onSuccess: async () => {
      await trpcUtil.testSchedule.get.invalidate({ testSuiteId });
      onSubmit();
      notifications.show({
        title: 'Schedule deleted',
        message: 'Your test will stop executing automatically',
      });
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        message: 'Failed to delete schedule',
      });
      console.error(error);
    },
  });

  const currentTimezoneIana = selectedTimezoneIana || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const form = useForm<InternalSchedule & { timezoneIana: string }>({
    initialValues: { ...cronToSchedule(selectedCronTab), timezoneIana: currentTimezoneIana },
    validate: {
      days: (value) => (value.length === 0 ? 'Please select at least one day' : null),
      times: (value) => (value.length === 0 ? 'Please select at least one time' : null),
    },
  });

  return (
    <form
      onSubmit={form.onSubmit(async ({ days, times, timezoneIana }) => {
        try {
          await upsertSchedule({ testSuiteId, cronTab: scheduleToCron({ days, times }), timezoneIana });
          onSubmit();
          notifications.show({
            color: 'green',
            title: 'Schedule saved',
            message: 'Your test will now run on the selected days and times',
          });
        } catch (error) {
          console.log('could not save schedule', error);
          notifications.show({
            color: 'red',
            message: 'Failed to save schedule',
          });
        }
      })}
    >
      <div className="flex flex-col gap-2">
        <DropdownChecklist
          {...form.getInputProps('days')}
          options={dayNames}
          placeholder="None"
          label="Days"
          className="w-52"
          abbreviations={dayNamesAbbreviations}
        />
        <div className="mt-2 flex w-full flex-row flex-wrap gap-2">
          <DropdownChecklist
            {...form.getInputProps('times')}
            options={timeOptions}
            placeholder="None"
            label="Times"
            className="w-48"
          />
          <Select
            {...form.getInputProps('timezoneIana')}
            data={timezoneList}
            searchable
            allowDeselect={false}
            className="w-72"
            label="Timezone"
            classNames={{
              input: 'text-ellipsis whitespace-nowrap overflow-hidden',
            }}
          />
        </div>
        <div className="pt-4">
          <Alert
            color="blue"
            variant="light"
            className="text-sm"
            icon={<IconAlertCircle size={16} />}
            title="Suite Settings"
          >
            <div className="flex flex-row items-center gap-2">
              <p className="flex-grow text-xs text-gray-500">
                Need to update notifications, retries, and other test configurations?
              </p>
              <Button
                variant="subtle"
                className="text-xs text-blue-500 hover:text-blue-600"
                onClick={onOpenSuiteSettings}
                rightSection={<IconExternalLink size={16} />}
              >
                Open Suite Settings
              </Button>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              These settings will apply to all runs, regardless of trigger (API, GitHub, Scheduled)
            </p>
          </Alert>
        </div>
        <div className="flex justify-end space-x-2">
          {selectedCronTab ?
            <Button
              loading={isDeletingSchedule}
              variant="default"
              onClick={() => deleteSchedule({ testSuiteId }).catch(() => undefined)}
              leftSection={<IconTrash size={15} />}
            >
              Delete
            </Button>
          : null}
          <Button
            loading={isUpsertingSchedule}
            type="submit"
            leftSection={<IconDeviceFloppy size={15} />}
          >
            Save
          </Button>
        </div>
      </div>
    </form>
  );
}
